import { useId, useState } from 'react'
import { Icon } from '../../components/ui/Icon'
import { Input } from '../../components/ui/Input'
import { Modal } from '../../components/ui/Modal'
import { Spinner } from '../../components/ui/Spinner'
import type { components } from '../../lib/api/schema.gen'
import { formatMoney } from '../cart/cart-money'
import {
  type ConfigDimensionSegment,
  type SkuPicks,
  computeRangePresets,
  formatValueToken,
  gateConfigSchema,
  isValueLegal,
  validateAndComposeSku,
} from '../catalog/configurator-sku'
import type { ConfigurableResolveService } from '../catalog/useConfigurableResolve'
import { useConfigurableResolve } from '../catalog/useConfigurableResolve'
// UID-401 N1 (Gate-5) — the shared `imageRef` validator; see
// `AddPartsModal.tsx`'s doc comment on `isSafePartImageRef` for the full
// rationale (this file's own row markup mirrors that one's exactly).
import { isSafePartImageRef } from './AddPartsModal'
import type { AddPartsCandidateRow } from './rfq-append-lines'
import { useAddPartsSearch } from './use-add-parts-search'

type Sourceable = components['schemas']['Sourceable']

export interface AddPartsToRfqModalProps {
  readonly open: boolean
  /** UID-457 item 2 (ADR-0147 D3) — the real, debounced candidate search
   * (`add-parts-search.ts`'s `searchAddPartsCandidates`), injected exactly as
   * `AddPartsModal.tsx` takes its own `search` prop. This modal owns
   * debounce/loading/error/abort-on-unmount via `useAddPartsSearch`, not the
   * caller. */
  readonly search: (query: string) => Promise<readonly AddPartsCandidateRow[]>
  /** Sourceable ids already on the RFQ — excluded from the candidate list, so
   * the modal never offers an `Add` that is a guaranteed `line-already-present`
   * 409 (ADR-0093 D4). */
  readonly alreadyOnRfq: ReadonlySet<string>
  /** True while a `POST /rfq/{rfqId}/lines` is in flight — every `Add` is
   * disabled, so a double-click cannot double-post. */
  readonly adding: boolean
  readonly onClose: () => void
  /** One part per activation: the design's modal has a per-row `Add` and a
   * `Done`, with no quantity control and no remove (ADR-0093 D2). */
  readonly onAdd: (sourceable: Sourceable) => void
  /** The design's footer "Upload new files" — closes the modal and hands off
   * to the CAD-intake screen (L3663). */
  readonly onUploadNewFiles: () => void
  /** Injectable for tests; defaults to the real BFF-backed
   * `GET /catalog/configurable/{sku}` (`useConfigurableResolve`'s own
   * default) — the seam a `kind: 'configurable'` candidate row's inline
   * "Configure" step resolves through. */
  readonly configurableResolveService?: ConfigurableResolveService
}

/**
 * "Add parts to this RFQ" (UID-265b) — the post-submit add-parts picker the
 * Edit RFQ control opens. Design source: `Fictiv Platform.dc.html`
 * L3630-3666 (`r.addPartsView`), reached from L3725's `onEditRfq` →
 * `openRfqDetailAdd()` (L8196). No navigation, no route change.
 *
 * DISTINCT from `AddPartsModal.tsx`, which is the COMPOSE screen's
 * (`/rfq/new`) draft-time picker: different design element (L3630-3666 vs.
 * the compose panel), different title/subtitle/footer, a search field the
 * compose one has no counterpart for, and — the substantive difference — a
 * per-row `Add` that writes to the server immediately via the append
 * endpoint, instead of a batched checkbox selection mutating local draft
 * state. The two share the candidate source (`rfq-append-lines.ts`'s SCOPE
 * NOTE) and this repo's ONE dialog primitive; they are not merged because
 * their interaction models genuinely differ.
 *
 * Built on the shared `Modal` (components/ui/Modal.tsx) rather than a
 * hand-rolled dialog, so it inherits that primitive's focus trap, `Escape`
 * close, backdrop dismissal, body portal, and focus-return-to-invoker —
 * including the ADR-0087 D4 fallback for an invoker that unmounted while the
 * modal was open (ADR-0093 D6 (4)).
 */
export function AddPartsToRfqModal({
  open,
  search,
  alreadyOnRfq,
  adding,
  onClose,
  onAdd,
  onUploadNewFiles,
  configurableResolveService,
}: AddPartsToRfqModalProps) {
  // `openRfqDetailAdd()` resets the search on every open (L8196:
  // `rfqDetailAddSearch:''`) — a stale filter from the previous visit would
  // silently hide most of the list. (`AddPartsModal.tsx` resets on CLOSE
  // instead — see `useAddPartsSearch`'s doc comment for why the two differ.)
  const { query, setQuery, state } = useAddPartsSearch({ open, search, resetOn: 'open' })

  if (!open) return null

  const results = state.status === 'ready' ? state.results : []
  const candidates = results.filter((candidate) => !alreadyOnRfq.has(candidate.sourceable.id))

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add parts to this RFQ"
      maxWidth={560}
      className="rfq-add-parts-modal"
      headerContent={
        <div>
          <h2 className="modal-title rfq-add-parts-title">Add parts to this RFQ</h2>
          <p className="rfq-add-parts-subtitle">
            Pull in catalog parts or existing library parts, or upload new files.
          </p>
        </div>
      }
      footer={
        <div className="rfq-add-parts-footer">
          <button
            type="button"
            className="rfq-add-parts-upload-btn"
            onClick={() => {
              onUploadNewFiles()
              onClose()
            }}
          >
            <Icon name="upload" /> Upload new files
          </button>
          <button type="button" className="rfq-add-parts-done-btn" onClick={onClose}>
            Done
          </button>
        </div>
      }
    >
      <div className="rfq-add-parts-search">
        <Icon name="search" className="rfq-add-parts-search-icon" />
        {/* Not the shared `Input` primitive: the design draws this as a bare
         * icon-inset search field with NO visible label (L3641), and `Input`
         * always renders a label row plus its own field chrome. The
         * accessible name is carried by `aria-label`, never by the
         * placeholder alone (WCAG 3.3.2 / 4.1.2). */}
        <input
          type="search"
          className="rfq-add-parts-search-input"
          aria-label="Search catalog or library parts"
          placeholder="Search catalog or library parts by name or MPN…"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
      </div>
      {/* UID-457 item 2 (1a) — same mutually-exclusive loading/error/empty
       * posture as `AddPartsModal.tsx`; see that file's doc comment. */}
      {state.status === 'loading' && (
        <p className="rfq-add-parts-loading">
          <Spinner size="sm" label="Searching…" />
        </p>
      )}
      {state.status === 'error' && (
        <p className="rfq-add-parts-error" role="alert">
          {state.message}
        </p>
      )}
      {state.status === 'ready' && candidates.length === 0 && (
        <p className="rfq-add-parts-empty">
          No matching parts. Try a different search, or upload new files below.
        </p>
      )}
      {state.status === 'ready' && candidates.length > 0 && (
        <ul className="rfq-add-parts-list">
          {candidates.map((candidate) => {
            const isConfigurable = candidate.sourceable.kind === 'configurable'
            return (
              <li key={candidate.sourceable.id} className="rfq-add-parts-row">
                {isConfigurable ? (
                  <ConfigurableCandidateRow
                    candidate={candidate}
                    adding={adding}
                    onAdd={onAdd}
                    {...(configurableResolveService !== undefined
                      ? { resolveService: configurableResolveService }
                      : {})}
                  />
                ) : (
                  <div className="rfq-add-parts-row-main">
                    {/* UID-401 item 2 / correction 2 (`dc.html:4449`) — mirrors
                     * `AddPartsModal.tsx`'s treatment exactly: a real 38×38
                     * cover-fit thumbnail when the candidate's Sourceable
                     * carries `imageRef` (ADR-0090 D2), decorative
                     * (`aria-hidden`) since the row's own
                     * `aria-label="Add {name}"` on the button below already
                     * carries the accessible name. No `imageRef` falls back
                     * to the existing empty placeholder box — never a
                     * fabricated/hard-coded path. */}
                    <span
                      className="rfq-add-parts-thumb"
                      aria-hidden="true"
                      style={
                        isSafePartImageRef(candidate.sourceable.imageRef)
                          ? {
                              backgroundImage: `url("${candidate.sourceable.imageRef.replace(/["\\]/g, '\\$&')}")`,
                            }
                          : undefined
                      }
                    />
                    <span className="rfq-add-parts-meta">
                      <span className="rfq-add-parts-name-row">
                        <span className="rfq-add-parts-name" title={candidate.name}>
                          {candidate.name}
                        </span>
                        <span
                          className={`rfq-add-parts-tag rfq-add-parts-tag-${candidate.tag.toLowerCase()}`}
                        >
                          {candidate.tag}
                        </span>
                      </span>
                      <span className="rfq-add-parts-sub">{candidate.partNumber ?? '—'}</span>
                    </span>
                    {/* `dc.html:4454` — the price is its OWN right-aligned
                     * element (`p.priceLabel`), not concatenated into the
                     * part-number subtitle line above. */}
                    <span className="rfq-add-parts-price">
                      {candidate.sourceable.indicativePrice
                        ? formatMoney(
                            candidate.sourceable.indicativePrice.amount,
                            candidate.sourceable.indicativePrice.currency,
                          )
                        : 'Quote required'}
                    </span>
                    {/* Repeated "Add" buttons need a per-row accessible name
                     * — a screen-reader user tabbing the list otherwise
                     * hears "Add" N times with nothing to distinguish them
                     * (WCAG 2.4.6). */}
                    <button
                      type="button"
                      className="rfq-add-parts-add-btn"
                      aria-label={`Add ${candidate.name}`}
                      disabled={adding}
                      onClick={() => onAdd(candidate.sourceable)}
                    >
                      Add
                    </button>
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </Modal>
  )
}

/** One `configSchema` dimension segment (`range`/`enum`), plus its INDEX
 * into `schema.segments` — mirrors `SeriesConfiguratorDrawer.tsx`'s own
 * `pickableDimensionEntries`, so a picked value can never be misaligned with
 * its segment when segments are interleaved with literals. Kept local to
 * this file rather than imported: that helper is private to its own module,
 * and this modal's row-scoped picker is deliberately a smaller, standalone
 * surface (see `ConfigurableCandidateRow`'s doc comment). */
function dimensionEntries(
  schema: NonNullable<AddPartsCandidateRow['configSchema']>,
): ReadonlyArray<{ readonly segment: ConfigDimensionSegment; readonly index: number }> {
  return schema.segments
    .map((segment, index) => ({ segment, index }))
    .filter(
      (entry): entry is { segment: ConfigDimensionSegment; index: number } =>
        entry.segment.kind === 'range' || entry.segment.kind === 'enum',
    )
}

/**
 * The user's explicit choice (2026-09-10, superseding the earlier disabled-
 * "Configure in the catalog" treatment this replaces): a `kind:
 * 'configurable'` candidate row's trailing action swaps to "Configure",
 * which expands the row IN PLACE to show that family's `configSchema`
 * dimensions (length/diameter/material, …) as inline pickers — no
 * navigation away from the modal, no separate series-configurator route.
 * Once every dimension has a legal pick, the composed SKU resolves via the
 * SAME `GET /catalog/configurable/{sku}` seam `ConfiguratorCore`/
 * `SeriesConfiguratorDrawer` use (`useConfigurableResolve`, injected here
 * only for tests), and the trailing action becomes "Add" for the resolved,
 * concrete Sourceable — the exact `onAdd(sourceable)` call the plain-Add
 * path already uses, never a second add mechanism.
 *
 * Deliberately NOT `ConfiguratorCore`/`SeriesConfiguratorDrawer` reused
 * wholesale: both are full-surface components (cart wiring, demo chrome,
 * a persistent footer, an outer tab strip) built for their own page/drawer
 * layout, not a compact list row inside an already-open modal. What IS
 * reused verbatim is the actual configurator LOGIC those two share —
 * `configurator-sku.ts`'s gate/validate/compose/format/preset functions and
 * the `useConfigurableResolve` resolve hook — so this row can never drift
 * from the same SKU-composition/legality rules the full-page surfaces
 * enforce.
 *
 * A row whose `configSchema` is absent (a library/plain-catalog row never
 * carries one) or `gateConfigSchema`-`disabled` (unresolvable / a `raw`
 * segment) falls back to the ORIGINAL honest disabled-Add + inline note —
 * there is genuinely nothing to configure inline for that shape, and
 * ADR-0090/0091 forbid fabricating pickers `configSchema` doesn't offer.
 *
 * Resolve failures (404 "not available at this value", network/other error)
 * render INLINE within this row's own expanded panel, never a modal-level
 * toast — scoped to the one row so the shopper can adjust and retry without
 * losing their place in the rest of the list (`role="alert"`, WCAG 4.1.3).
 */
function ConfigurableCandidateRow({
  candidate,
  adding,
  onAdd,
  resolveService,
}: {
  readonly candidate: AddPartsCandidateRow
  readonly adding: boolean
  readonly onAdd: (sourceable: Sourceable) => void
  /** Injectable for tests; defaults to the real BFF-backed resolve (via
   * `useConfigurableResolve`'s own default). */
  readonly resolveService?: ConfigurableResolveService
}) {
  const [expanded, setExpanded] = useState(false)
  const [picks, setPicks] = useState<SkuPicks>(new Map())
  const inputIdBase = useId()

  const schema = candidate.configSchema
  const gate = schema ? gateConfigSchema(schema) : { kind: 'disabled' as const }
  const entries = schema && gate.kind === 'pickable' ? dimensionEntries(schema) : []

  const sku = expanded && schema ? validateAndComposeSku(schema, picks) : null
  const resolveState = useConfigurableResolve(sku, resolveService)
  const resolvedSourceable = resolveState.status === 'resolved' ? resolveState.sourceable : null

  function pick(index: number, value: number | null) {
    setPicks((prev) => {
      const next = new Map(prev)
      if (value === null) next.delete(index)
      else next.set(index, value)
      return next
    })
  }

  function cancel() {
    setExpanded(false)
    setPicks(new Map())
  }

  const displayPrice = resolvedSourceable?.indicativePrice ?? candidate.sourceable.indicativePrice

  return (
    <>
      <div className="rfq-add-parts-row-main">
        <span
          className="rfq-add-parts-thumb"
          aria-hidden="true"
          style={
            isSafePartImageRef(candidate.sourceable.imageRef)
              ? {
                  backgroundImage: `url("${candidate.sourceable.imageRef.replace(/["\\]/g, '\\$&')}")`,
                }
              : undefined
          }
        />
        <span className="rfq-add-parts-meta">
          <span className="rfq-add-parts-name-row">
            <span className="rfq-add-parts-name" title={candidate.name}>
              {candidate.name}
            </span>
            <span className={`rfq-add-parts-tag rfq-add-parts-tag-${candidate.tag.toLowerCase()}`}>
              {candidate.tag}
            </span>
          </span>
          <span className="rfq-add-parts-sub">
            {resolvedSourceable?.catalogSku ?? candidate.partNumber ?? '—'}
          </span>
          {/* The genuinely-not-configurable-here fallback (no usable
           * `configSchema`) — same tone/copy as the original disabled
           * treatment this replaces for a pickable schema. */}
          {gate.kind === 'disabled' && (
            <span className="rfq-add-parts-configurable-note">
              Configure this part in the catalog before adding
            </span>
          )}
        </span>
        <span className="rfq-add-parts-price">
          {displayPrice
            ? formatMoney(displayPrice.amount, displayPrice.currency)
            : 'Quote required'}
        </span>
        {gate.kind === 'disabled' ? (
          <button
            type="button"
            className="rfq-add-parts-add-btn"
            aria-label={`Add ${candidate.name} — configure this part in the catalog first`}
            disabled
          >
            Add
          </button>
        ) : !expanded ? (
          <button
            type="button"
            className="rfq-add-parts-add-btn"
            aria-label={`Configure ${candidate.name}`}
            disabled={adding}
            onClick={() => setExpanded(true)}
          >
            Configure
          </button>
        ) : (
          <div className="rfq-add-parts-configure-actions">
            <button
              type="button"
              className="rfq-add-parts-configure-cancel-btn"
              aria-label={`Cancel configuring ${candidate.name}`}
              onClick={cancel}
            >
              Cancel
            </button>
            {resolvedSourceable && (
              <button
                type="button"
                className="rfq-add-parts-add-btn"
                aria-label={`Add ${candidate.name}`}
                disabled={adding}
                onClick={() => onAdd(resolvedSourceable)}
              >
                Add
              </button>
            )}
          </div>
        )}
      </div>

      {expanded && schema && gate.kind === 'pickable' && (
        <div className="rfq-add-parts-configure-panel">
          {entries.map(({ segment, index }) => (
            <fieldset key={index} className="rfq-add-parts-configure-field">
              <legend>{segment.fieldName ?? 'Value'}</legend>
              {segment.kind === 'enum' && (
                <div className="rfq-add-parts-configure-pills">
                  {segment.values.map((value) => {
                    const selected = picks.get(index) === value
                    return (
                      <button
                        key={value}
                        type="button"
                        aria-pressed={selected}
                        className={`rfq-add-parts-configure-pill${
                          selected ? ' rfq-add-parts-configure-pill-selected' : ''
                        }`}
                        onClick={() => pick(index, selected ? null : value)}
                      >
                        {formatValueToken(segment, value)}
                      </button>
                    )
                  })}
                </div>
              )}
              {segment.kind === 'range' && (
                <RangeControl
                  segment={segment}
                  label={segment.fieldName ?? 'Value'}
                  idBase={`${inputIdBase}-${index}`}
                  value={picks.get(index) ?? null}
                  onChange={(value) => pick(index, value)}
                />
              )}
            </fieldset>
          ))}

          <div className="rfq-add-parts-configure-status" aria-live="polite">
            {resolveState.status === 'loading' && (
              <span className="rfq-add-parts-configure-checking">
                <Spinner size="sm" aria-hidden="true" />
                Checking availability…
              </span>
            )}
            {resolveState.status === 'not-available' && (
              <p role="alert" className="rfq-add-parts-configure-error">
                Not available at that combination. Try a different value.
              </p>
            )}
            {resolveState.status === 'error' && (
              <p role="alert" className="rfq-add-parts-configure-error">
                {resolveState.message}
              </p>
            )}
          </div>
        </div>
      )}
    </>
  )
}

type RangeSegment = Extract<ConfigDimensionSegment, { kind: 'range' }>

/** The row-scoped counterpart of `ConfiguratorCore`'s own range picker
 * (quick-pick presets + a bounded free-text input) — same
 * `computeRangePresets`/`formatValueToken`/`isValueLegal` legality rules,
 * trimmed to what one inline row needs. */
function RangeControl({
  segment,
  label,
  idBase,
  value,
  onChange,
}: {
  readonly segment: RangeSegment
  readonly label: string
  readonly idBase: string
  readonly value: number | null
  readonly onChange: (value: number | null) => void
}) {
  const [raw, setRaw] = useState(value !== null ? formatValueToken(segment, value) : '')
  const presets = computeRangePresets(segment)

  return (
    <div className="rfq-add-parts-configure-range">
      <div className="rfq-add-parts-configure-pills">
        {presets.map((preset) => {
          const selected = value === preset
          return (
            <button
              key={preset}
              type="button"
              aria-pressed={selected}
              className={`rfq-add-parts-configure-pill${
                selected ? ' rfq-add-parts-configure-pill-selected' : ''
              }`}
              onClick={() => {
                setRaw(formatValueToken(segment, preset))
                onChange(preset)
              }}
            >
              {formatValueToken(segment, preset)}
            </button>
          )
        })}
      </div>
      <Input
        label={label}
        hideLabel
        className="rfq-add-parts-configure-input"
        id={idBase}
        type="number"
        inputMode="decimal"
        min={segment.min}
        max={segment.max}
        step={segment.step}
        placeholder={`${formatValueToken(segment, segment.min)}–${formatValueToken(segment, segment.max)}`}
        value={raw}
        error={
          value !== null && !isValueLegal(segment, value)
            ? `Enter a value between ${segment.min} and ${segment.max}`
            : undefined
        }
        onChange={(event) => {
          const text = event.target.value
          setRaw(text)
          const parsed = text.trim() === '' ? null : Number(text)
          onChange(parsed !== null && Number.isFinite(parsed) ? parsed : null)
        }}
      />
    </div>
  )
}
